import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Inbox, Loader2, Pencil, Plus, Trash2 } from 'lucide-react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/shared/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AllCountriesSelect } from '@/features/installations/components/all-countries-select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  SupplierInstallationDeleteConfirmDialog,
  type PendingSupplierInstallationDelete,
} from '@/features/suppliers/components/supplier-installation-delete-confirm-dialog'
import { SourceGroupReferenceBlockedDialog } from '@/features/installations/components/source-group-reference-blocked-dialog'
import { isSourceGroupReferenceConflict, blockedSourceGroupCount } from '@/features/installations/lib/is-source-group-reference-conflict'
import { precursorCnCodeSourceGroupsPath } from '@/features/installations/lib/precursor-cn-code-source-groups-path'
import { handleServerError } from '@/shared/lib/handle-server-error'
import {
  useSupplierInstallationMutations,
  useSupplierInstallationsQuery,
} from '@/features/suppliers/hooks/use-supplier-installations'
import {
  isSupplierInstallationNameTooLong,
  isSupplierInstallationNameValid,
} from '@/features/suppliers/lib/supplier-installation-name'
import { DialogPortalContainer } from '@/shared/lib/dialog-portal-container'
import { preventDialogDismissForCombobox } from '@/shared/lib/dialog-combobox'

type SupplierInstallationsSectionCardProps = {
  supplierOrganizationId: string
  canEdit: boolean
  variant?: 'card' | 'plain'
}

export function SupplierInstallationsSectionCard({
  supplierOrganizationId,
  canEdit,
  variant = 'card',
}: SupplierInstallationsSectionCardProps) {
  const { t } = useTranslation(['installations', 'common'])
  const { installationId } = useParams<{ installationId: string }>()
  const navigate = useNavigate()
  const listQuery = useSupplierInstallationsQuery(supplierOrganizationId)
  const mutations = useSupplierInstallationMutations(supplierOrganizationId)

  const [createOpen, setCreateOpen] = useState(false)
  const [installationName, setInstallationName] = useState('')
  const [installationCountry, setInstallationCountry] = useState('')
  const [pendingDelete, setPendingDelete] = useState<PendingSupplierInstallationDelete | null>(
    null
  )
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [sourceGroupBlocked, setSourceGroupBlocked] =
    useState<PendingSupplierInstallationDelete | null>(null)

  const rows = listQuery.data
  const createNameTooLong = isSupplierInstallationNameTooLong(installationName)
  const isCreateNameValid = isSupplierInstallationNameValid(installationName)
  const isCreateCountryValid = installationCountry.trim().length === 2
  const isCreateFormValid = isCreateNameValid && isCreateCountryValid
  const createNameError = createNameTooLong
    ? t('installations:suppliers.edit.installations.nameMaxLengthError')
    : undefined

  const resetCreateForm = () => {
    setInstallationName('')
    setInstallationCountry('')
  }

  const submitCreate = async () => {
    const country = installationCountry.trim()
    if (!isCreateFormValid || country.length !== 2) {
      return
    }
    try {
      const created = await mutations.addSupplierInstallation.mutateAsync({
        name: installationName.trim(),
        country: country.toLowerCase(),
      })
      resetCreateForm()
      setCreateOpen(false)
      toast.success(
        t('installations:suppliers.edit.installations.notifications.createSuccess')
      )
      if (installationId) {
        navigate(
          `/dashboard/${installationId}/suppliers/${supplierOrganizationId}/installations/${created.id}/edit`
        )
      }
    } catch {
      toast.error(t('installations:suppliers.edit.installations.notifications.createError'))
    }
  }

  const requestDelete = (item: PendingSupplierInstallationDelete) => {
    if (item.sourceGroupCount > 0) {
      setSourceGroupBlocked(item)
      return
    }
    setPendingDelete(item)
    setDeleteDialogOpen(true)
  }

  const clearPendingDelete = () => {
    setPendingDelete(null)
    setDeleteDialogOpen(false)
  }

  const handleConfirmDelete = async () => {
    if (!pendingDelete) {
      return
    }
    try {
      await mutations.deleteSupplierInstallation.mutateAsync(pendingDelete.id)
      clearPendingDelete()
    } catch (error) {
      if (isSourceGroupReferenceConflict(error)) {
        const row = rows?.find((item) => item.id === pendingDelete.id)
        const blocked = {
          ...pendingDelete,
          sourceGroupCount: blockedSourceGroupCount(row?.sourceGroupCount),
          sourceGroupReferenceTarget: row?.sourceGroupReferenceTarget ?? null,
        }
        clearPendingDelete()
        setSourceGroupBlocked(blocked)
        return
      }
      handleServerError(error)
    }
  }

  const createButton =
    canEdit && rows !== undefined && rows.length > 0 ? (
      <Button type="button" onClick={() => setCreateOpen(true)}>
        <Plus className="size-4" />
        {t('common:actions.create')}
      </Button>
    ) : null

  const listContent = (
    <>
          {listQuery.isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="size-8 animate-spin text-muted-foreground" />
            </div>
          ) : rows === undefined ? (
            <p className="text-muted-foreground">
              {t('installations:suppliers.edit.installations.loadError')}
            </p>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-4 rounded-md border border-dashed py-12">
              <Inbox className="size-10 text-muted-foreground/50" />
              <div className="space-y-1 text-center">
                <h3 className="text-lg font-semibold">
                  {t('installations:suppliers.edit.installations.emptyTitle')}
                </h3>
                <p className="max-w-md text-sm text-muted-foreground">
                  {t('installations:suppliers.edit.installations.emptyDescription')}
                </p>
              </div>
              {canEdit && (
                <Button type="button" onClick={() => setCreateOpen(true)}>
                  <Plus className="size-4" />
                  {t('common:actions.create')}
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-hidden rounded-md border">
              <Table className="table-fixed">
                <TableHeader>
                  <TableRow className="group/row">
                    <TableHead
                      className={cn(
                        'bg-background group-hover/row:bg-muted',
                        'min-w-0 max-w-[75%] whitespace-normal'
                      )}
                    >
                      {t('installations:configuration.table.name')}
                    </TableHead>
                    {canEdit && (
                      <TableHead
                        className={cn(
                          'bg-background group-hover/row:bg-muted',
                          'w-[120px] min-w-0 max-w-[120px] text-end'
                        )}
                      >
                        {t('installations:configuration.table.actions')}
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id} className="group/row">
                      <TableCell
                        className={cn(
                          'bg-background group-hover/row:bg-muted',
                          'min-w-0 max-w-[75%] whitespace-normal'
                        )}
                      >
                        <div className="flex flex-col gap-1">
                          <span>{row.name}</span>
                          {row.localName ? (
                            <span className="text-sm text-muted-foreground">{row.localName}</span>
                          ) : null}
                        </div>
                      </TableCell>
                      {canEdit && (
                        <TableCell
                          className={cn(
                            'bg-background group-hover/row:bg-muted',
                            'w-[120px] min-w-0 max-w-[120px] text-end'
                          )}
                        >
                          <div className="flex justify-end gap-1">
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              aria-label={t(
                                'installations:suppliers.edit.installations.a11y.editInstallation',
                                { name: row.name }
                              )}
                              asChild
                            >
                              <Link
                                to={`/dashboard/${installationId}/suppliers/${supplierOrganizationId}/installations/${row.id}/edit`}
                              >
                                <Pencil className="size-4" />
                              </Link>
                            </Button>
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="text-destructive"
                              aria-label={t(
                                'installations:suppliers.edit.installations.a11y.deleteInstallation',
                                { name: row.name }
                              )}
                              onClick={() =>
                                requestDelete({
                                  id: row.id,
                                  label: row.name,
                                  sourceGroupCount: row.sourceGroupCount,
                                  sourceGroupReferenceTarget: row.sourceGroupReferenceTarget,
                                })
                              }
                              disabled={mutations.deleteSupplierInstallation.isPending}
                            >
                              <Trash2 className="size-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
    </>
  )

  return (
    <>
      {variant === 'plain' ? (
        <div className="space-y-4">
          {createButton ? <div className="flex justify-end">{createButton}</div> : null}
          {listContent}
        </div>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <CardTitle>{t('installations:suppliers.edit.installations.title')}</CardTitle>
                <p className="mt-1 text-sm font-normal text-muted-foreground">
                  {t('installations:suppliers.edit.installations.description')}
                </p>
              </div>
              {createButton}
            </div>
          </CardHeader>
          <CardContent>{listContent}</CardContent>
        </Card>
      )}

      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open)
          if (!open) {
            resetCreateForm()
          }
        }}
      >
        <DialogContent
          onInteractOutside={preventDialogDismissForCombobox}
          onPointerDownOutside={preventDialogDismissForCombobox}
        >
          <DialogPortalContainer>
            <DialogHeader>
              <DialogTitle>
                {t('installations:suppliers.edit.installations.dialog.addInstallation')}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid gap-2">
                <Label htmlFor="supplier-installation-create-name">
                  {t('installations:configuration.table.name')}
                  <span className="text-destructive"> *</span>
                </Label>
                <Input
                  id="supplier-installation-create-name"
                  placeholder={t('installations:suppliers.edit.installations.namePlaceholder')}
                  value={installationName}
                  onChange={(event) => setInstallationName(event.target.value)}
                  aria-required
                  aria-invalid={Boolean(createNameError)}
                />
                {createNameError ? (
                  <p className="text-sm text-destructive">{createNameError}</p>
                ) : null}
              </div>
              <div className="grid gap-2">
                <Label htmlFor="supplier-installation-create-country">
                  {t('common:inputFields.country.label')}
                  <span className="text-destructive"> *</span>
                </Label>
                <AllCountriesSelect
                  id="supplier-installation-create-country"
                  value={installationCountry}
                  onValueChange={setInstallationCountry}
                  placeholder={t(
                    'installations:suppliers.edit.installations.dialog.countryPlaceholder'
                  )}
                />
              </div>
            </div>
          </DialogPortalContainer>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setCreateOpen(false)
                resetCreateForm()
              }}
            >
              {t('installations:configuration.cancel')}
            </Button>
            <Button
              type="button"
              disabled={!isCreateFormValid || mutations.addSupplierInstallation.isPending}
              onClick={submitCreate}
            >
              {t('common:actions.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SupplierInstallationDeleteConfirmDialog
        pendingDelete={pendingDelete}
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            clearPendingDelete()
          } else {
            setDeleteDialogOpen(true)
          }
        }}
        onConfirm={handleConfirmDelete}
        isLoading={mutations.deleteSupplierInstallation.isPending}
      />

      <SourceGroupReferenceBlockedDialog
        open={sourceGroupBlocked !== null}
        onOpenChange={(open) => {
          if (!open) {
            setSourceGroupBlocked(null)
          }
        }}
        title={t(
          'installations:suppliers.edit.installations.sourceGroupReferenceBlocked.title'
        )}
        description={t(
          'installations:suppliers.edit.installations.sourceGroupReferenceBlocked.description',
          {
            label: sourceGroupBlocked?.label ?? '',
            count: sourceGroupBlocked?.sourceGroupCount ?? 0,
          }
        )}
        manageSourceGroupsHref={
          sourceGroupBlocked?.sourceGroupReferenceTarget
            ? precursorCnCodeSourceGroupsPath(
                sourceGroupBlocked.sourceGroupReferenceTarget
              )
            : undefined
        }
        manageSourceGroupsLabel={t(
          'installations:configuration.sourceGroupReferenceBlocked.manageSourceGroups'
        )}
      />
    </>
  )
}
